import { useContext, useRef, useEffect, useState, useCallback } from 'react';
import {
  keepSessionAlive,
  makePrivateClient,
  setOnTokenRefresh,
  type TokenRefreshResponse,
} from '@/utils/apollo';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { ApolloProvider, ApolloClient, NormalizedCacheObject } from '@apollo/client';

// Refresh every 4 minutes — stays ahead of the ~5 min access token lifetime
// so the session is extended before the token expires.
const GLOBAL_KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000;

/**
 * Exposes an instance of Apollo Client with authentication using a split link via sockets for
 * subscriptions and HTTP for everything else (queries and mutations).
 *
 * HTTP requests use session cookies for auth. WebSocket connections pass the JWT token
 * in params since cookies aren't reliably forwarded during WebSocket upgrades.
 *
 * The client is recreated when the JWT changes (e.g., after login or token refresh).
 *
 * Token Refresh Flow:
 * 1. When a 401 is received, apollo.ts calls /auth/refresh
 * 2. If successful, it notifies this provider via setOnTokenRefresh callback
 * 3. This provider updates the JWT and recreates the Apollo client with fresh token
 * 4. WebSocket connections are re-established with the new token
 */
export const PrivateApolloProvider = ({ children }: { children: React.ReactNode }) => {
  const { userInfo, setUserInfo } = useContext(UserInfoContext);
  const jwt = userInfo?.jwt || '';
  const previousJwtRef = useRef<string>(jwt);
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>(() =>
    makePrivateClient(jwt)
  );

  // Handle token refresh notifications from apollo.ts
  const handleTokenRefresh = useCallback(
    (response: TokenRefreshResponse) => {
      console.log('🔄 Token refresh notification received, updating JWT');

      // Update userInfo with new JWT
      if (userInfo && response.access_token !== userInfo.jwt) {
        setUserInfo({
          ...userInfo,
          jwt: response.access_token,
        });
      }
    },
    [userInfo, setUserInfo]
  );

  // Register the token refresh callback on mount
  useEffect(() => {
    setOnTokenRefresh(handleTokenRefresh);

    // Cleanup on unmount
    return () => {
      setOnTokenRefresh(null);
    };
  }, [handleTokenRefresh]);

  // Global session keepalive — extends the server-side session by calling
  // /auth/refresh periodically. This triggers a real Keycloak token exchange
  // which pushes session.expires_at forward. Without this, long-lived SPA pages
  // (like the workflow canvas) make no HTTP requests while the user edits, and
  // the session silently expires.
  useEffect(() => {
    if (!jwt) return; // Not authenticated

    const id = setInterval(() => {
      keepSessionAlive();
    }, GLOBAL_KEEPALIVE_INTERVAL_MS);

    return () => clearInterval(id);
  }, [jwt]);

  // Recreate Apollo client when JWT changes
  useEffect(() => {
    // Only recreate client if JWT actually changed
    if (jwt !== previousJwtRef.current) {
      console.log('🔄 JWT changed, recreating Apollo client with new WebSocket connection');
      previousJwtRef.current = jwt;

      // Stop the old client to close WebSocket connections
      client.stop();

      // Create new client with updated JWT
      setClient(makePrivateClient(jwt));
    }
  }, [jwt, client]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
